<script>
import { Search, Plus, X, ChevronDown, ChevronsUpDown } from "lucide-vue-next";
import TableFooterWrapper from "@/shared/ui/custom/components/TableFooterWrapper.vue";
import dateFormat from "@/shared/lib/date-format";
import { getTariffList, removeTariff } from "@/entities/tariff";
import {
  LocalityTypeLabel,
  ParticipantTypeLabel,
  TypeCalculationLabel,
} from "@/shared/config/tariffs";
import { getReferenceListByCodes } from "@/entities/directory";
import BaseTabs from "@/shared/ui/custom/tabs/BaseTabs.vue";
import { getDirectoryItem } from "@/entities/directory";

import Button from "@/shared/ui/button/Button.vue";
import { Input } from "@/shared/ui/input";
import { Badge } from "@/shared/ui/badge";
import { Checkbox } from "@/shared/ui/checkbox";
import { Popover, PopoverTrigger, PopoverContent } from "@/shared/ui/popover";
import TableRowActions from "@/shared/ui/custom/components/TableRowActions.vue";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { Spinner } from "@/shared/ui/spinner";

export default {
  name: "TariffTable",
  components: {
    BaseTabs,
    TableFooterWrapper,
    Search,
    Plus,
    X,
    ChevronDown,
    ChevronsUpDown,
    Button,
    Input,
    Badge,
    Checkbox,
    Popover,
    PopoverTrigger,
    PopoverContent,
    TableRowActions,
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationNext,
    PaginationPrevious,
    Table,
    TableBody,
    TableCell,
    TableEmpty,
    TableHead,
    TableHeader,
    TableRow,
    Spinner,
  },
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      filters: { name__ilike: "", page: 1, size: 20, codes: [] },
      pageSizes: [1, 5, 10, 20],

      references: [],
      activeTab: "tariff_list",

      template_categories: [],
      template_loading: false,

      codesSearchQuery: "",
      codesPopoverOpen: false,
    };
  },
  computed: {
    actions() {
      const list = [];
      if (this.$store.getters.has_access("TUD")) {
        list.push({
          label: this.$t("Редактировать"),
          type: "default",
          action: "edit",
          icon: "huge-pen2",
        });
      }
      if (this.$store.getters.has_access("TDD")) {
        list.push({
          label: this.$t("Удалить"),
          type: "danger",
          action: "remove",
          icon: "huge-danger",
        });
      }
      list.push({
        label: this.$t("Просмотр тарифа"),
        type: "default",
        action: "view",
        icon: "huge-eye",
        isLast: true,
      });
      return list;
    },
    canCreateTariff() {
      return this.$store.getters.has_access("TCD");
    },
    columns() {
      return [
        { label: "Тип лица", prop: "participant_type", minWidth: 200 },
        { label: "Местность", prop: "locality", minWidth: 200 },
        { label: "Родственник", prop: "relatives", minWidth: 150 },
        { label: "Сумма договора", prop: "amount", minWidth: 150 },
        { label: "Ставка тарифа", prop: "value", minWidth: 150 },
      ];
    },
    tabs() {
      const tabs = {};
      if (this.$store.getters.has_access("TVD")) {
        tabs.tariff_list = { label: this.$t("Тарифы для НД") };
        tabs.benefit_list = { label: this.$t("Льготы") };
      }
      if (this.$store.getters.has_access("MVD")) {
        tabs.mrp_list = { label: this.$t("МРП") };
      }
      return tabs;
    },
    filteredCategories() {
      if (!this.codesSearchQuery) return this.template_categories;
      const q = this.codesSearchQuery.toLowerCase();
      return this.template_categories.filter((item) =>
        item.name?.toLowerCase().includes(q)
      );
    },
    selectedCodesLabel() {
      if (!this.filters.codes.length) return "";
      const first = this.template_categories.find(
        (c) => c.code === this.filters.codes[0]
      );
      const label = first?.name || this.filters.codes[0];
      if (this.filters.codes.length === 1) return label;
      return label;
    },
  },
  watch: {
    filters: { handler: "getTariffList", deep: true },
  },
  async mounted() {
    await this.getTariffList();
    await this.loadDirectories();
  },
  methods: {
    async getTariffList() {
      this.loading = true;
      try {
        const params = {
          size: this.filters.size,
          page: this.filters.page,
          ...(this.filters.name__ilike
            ? { name__ilike: this.filters.name__ilike.trim().toLowerCase() }
            : {}),
        };

        if (this.filters.codes.length) {
          params["codes"] = this.filters.codes.join(",");
        }
        const data = await getTariffList(params);
        if (data?.success && data.payload) {
          this.total = data.payload.total;
          this.filters.size = data.payload.size;
          this.filters.page = data.payload.page;
          this.list = data.payload.items || [];

          const all_codes = data.payload.items
            .map((item) => item.codes)
            .filter((code) => code != null)
            .flat();

          const codes = [...new Set([...all_codes])];
          if (codes.length) {
            await this.loadReferences(codes);
          }
        }
      } finally {
        this.loading = false;
      }
    },
    async loadReferences(codes) {
      const data = await getReferenceListByCodes({ codes });
      if (data?.success) {
        this.references = data.payload?.items || [];
      }
    },
    handlePageChange(page) {
      this.filters.page = page;
    },
    goToCreate() {
      this.$router.push({ name: "tariff_new", params: { id: "new" } });
    },
    dateFormat(date) {
      if (!date) return this.$t("Не указано");
      return dateFormat.timestamp_to_local_tz(date, "DD.MM.YYYY, HH:mm");
    },
    async handleActions(item) {
      switch (item.action) {
        case "edit":
          this.$router.push({ name: "tariff_item", params: { id: item.uuid } });
          break;
        case "view":
          this.$router.push({
            name: "tariff_item_view",
            params: { id: item.uuid },
          });
          break;
        case "remove":
          await this.removeTariff(item.uuid);
          break;
      }
    },
    async removeTariff(id) {
      const data = await removeTariff(id);
      if (data?.success) await this.getTariffList();
    },
    handleRowClick(row) {
      const canEdit = this.$store.getters.has_access("TUD");

      this.$router.push({
        name: canEdit ? "tariff_item" : "tariff_item_view",
        params: { id: row.id },
      });
    },
    search() {
      this.filters.page = 1;
    },
    renderCell(row, column) {
      if (column.type === "codes") {
        return row?.codes?.length ? `+${row.codes.length}` : "";
      }
      if (column.prop === "status") {
        return row.status === "ACTIVE"
          ? this.$t("Активный")
          : this.$t("Неактивный");
      }
      if (column.prop === "created_at") {
        return this.dateFormat(row.created_at);
      }

      if (column.prop === "participant_type") {
        return ParticipantTypeLabel[row.participant_type];
      }
      if (column.prop === "locality") {
        return LocalityTypeLabel[row.locality];
      }

      if (column.prop === "amount") {
        return `${this.getNumberValue(row.amount_from)} - ${this.getNumberValue(
          row.amount_to
        )}`;
      }
      if (column.prop === "relatives") {
        return row.relatives ? this.$t("Да") : this.$t("Нет");
      }

      if (column.prop === "value") {
        return `${this.getNumberValue(row.value)} ${
          TypeCalculationLabel[row.type_calculation]
        }`;
      }

      return row[column.prop];
    },
    getReferenceLabel(code) {
      if (!code) return "";
      const reference = this.references.find((item) => item.code === code);
      return reference?.name || "";
    },
    changeTab(tab) {
      this.$router.push({
        name: tab,
      });
    },
    getNumberValue(value) {
      return Number(value || 0);
    },

    async loadDirectories() {
      this.template_loading = true;
      try {
        const { success, payload } = await getDirectoryItem(
          "notarial_action_category"
        );

        if (success && payload) {
          this.template_categories = payload.children;
        }
      } catch (error) {
        console.error("Error loading directories:", error);
      } finally {
        this.template_loading = false;
      }
    },

    toggleCode(code) {
      const index = this.filters.codes.indexOf(code);
      if (index === -1) {
        this.filters.codes.push(code);
      } else {
        this.filters.codes.splice(index, 1);
      }
    },
    isCodeSelected(code) {
      return this.filters.codes.includes(code);
    },
    clearCodes() {
      this.filters.codes = [];
    },
  },
};
</script>

<template>
  <div class="wrapper-page">
    <div class="flex flex-col h-full w-full gap-8">
      <BaseTabs
        :tabs="tabs"
        :active-tab="activeTab"
        @change:tab="changeTab"
      ></BaseTabs>
      <div class="flex justify-between gap-5">
        <div class="flex gap-3">
          <div class="relative" style="width: 320px">
            <Search
              class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
            />
            <Input
              v-model="filters.name__ilike"
              :placeholder="$t('Поиск по тарифам')"
              class="pl-9"
              @input="search"
            />
          </div>

          <Popover v-model:open="codesPopoverOpen">
            <PopoverTrigger as-child>
              <button type="button" class="codes-trigger">
                <Search class="w-4 h-4 text-muted-foreground shrink-0" />
                <span
                  v-if="!filters.codes.length"
                  class="text-muted-foreground text-sm"
                >
                  {{ $t("Поиск по действиям") }}
                </span>
                <template v-else>
                  <span class="text-sm truncate">{{ selectedCodesLabel }}</span>
                  <Badge
                    v-if="filters.codes.length > 1"
                    variant="secondary"
                    class="text-xs"
                  >
                    +{{ filters.codes.length - 1 }}
                  </Badge>
                </template>
                <span class="ml-auto flex items-center gap-1 shrink-0">
                  <button
                    v-if="filters.codes.length"
                    type="button"
                    class="rounded-sm opacity-50 hover:opacity-100"
                    @click.stop.prevent="clearCodes"
                  >
                    <X class="w-3.5 h-3.5" />
                  </button>
                  <ChevronsUpDown class="w-4 h-4 opacity-50" />
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent class="w-80 p-0" align="start">
              <div class="p-2 border-b border-border">
                <div class="relative">
                  <Search
                    class="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
                  />
                  <Input
                    v-model="codesSearchQuery"
                    :placeholder="$t('Поиск...')"
                    class="pl-8 h-8"
                  />
                </div>
              </div>
              <div v-if="template_loading" class="flex justify-center py-4">
                <Spinner size="sm" />
              </div>
              <div
                v-else-if="!filteredCategories.length"
                class="py-4 text-center text-sm text-muted-foreground"
              >
                {{ $t("Ничего не найдено") }}
              </div>
              <div v-else class="max-h-60 overflow-y-auto p-1">
                <div
                  v-for="item in filteredCategories"
                  :key="item.code"
                  class="flex items-center gap-2 px-2 py-1.5 rounded-sm cursor-pointer hover:bg-accent text-sm"
                  @click="toggleCode(item.code)"
                >
                  <Checkbox
                    :model-value="isCodeSelected(item.code)"
                    @click.stop
                    @change="toggleCode(item.code)"
                  />
                  <span class="truncate">{{ item.name }}</span>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <Button v-if="canCreateTariff" variant="default" @click="goToCreate">
          {{ $t("Создать тариф") }} +
        </Button>
      </div>

      <div class="table-area">
        <div class="h-full overflow-auto">
          <Table :scroll-reset-key="filters.page">
            <TableHeader>
              <TableRow>
                <TableHead class="w-[68px]"></TableHead>
                <TableHead class="min-w-[300px]">{{
                  $t("Категория нотариального действия")
                }}</TableHead>
                <TableHead class="min-w-[330px]">{{
                  $t("Название тарифа")
                }}</TableHead>
                <TableHead
                  v-for="col in columns"
                  :key="col.prop"
                  :style="col.minWidth ? { minWidth: col.minWidth + 'px' } : {}"
                >
                  {{ $t(col.label) }}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableEmpty v-if="loading" :colspan="3 + columns.length">
                <Spinner size="sm" />
              </TableEmpty>
              <TableEmpty
                v-else-if="!list.length"
                :colspan="3 + columns.length"
              >
                {{ $t("Нет данных") }}
              </TableEmpty>
              <TableRow
                v-if="!loading"
                v-for="row in list"
                :key="row.id"
                class="cursor-pointer"
                @click="handleRowClick(row)"
              >
                <TableCell>
                  <TableRowActions
                    :actions="actions"
                    @action="
                      (item) =>
                        handleActions({ action: item.action, uuid: row.id })
                    "
                  />
                </TableCell>
                <TableCell>
                  <div class="flex gap-2">
                    <template v-if="row.codes?.length">
                      <div>
                        {{ getReferenceLabel(row.codes[0]) }}
                      </div>

                      <Popover v-if="row.codes.length > 1">
                        <PopoverTrigger as-child>
                          <div class="trigger" @click.stop>
                            <Badge variant="secondary"
                              >+{{ row.codes.length - 1 }}</Badge
                            >
                          </div>
                        </PopoverTrigger>
                        <PopoverContent class="w-[472px] p-0">
                          <ul class="content">
                            <li
                              v-for="option in row.codes"
                              :key="option"
                              class="popover-items"
                            >
                              {{ getReferenceLabel(option) }}
                            </li>
                          </ul>
                        </PopoverContent>
                      </Popover>
                    </template>
                    <template v-else>
                      {{ $t("Не указано") }}
                    </template>
                  </div>
                </TableCell>
                <TableCell>
                  {{ row.name }}
                </TableCell>
                <TableCell v-for="col in columns" :key="col.prop">
                  {{ renderCell(row, col) }}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <TableFooterWrapper
          :total="total"
          v-model:pageSize="filters.size"
          v-model:currentPage="filters.page"
          :pageSizes="[1, 5, 10, 20]"
          :page-size="filters.size"
        >
          <template #pagination>
            <Pagination
              v-if="total > filters.size"
              :total="total"
              :items-per-page="filters.size"
              :page="filters.page"
              :sibling-count="1"
              show-edges
              @update:page="handlePageChange"
            >
              <PaginationContent v-slot="{ items }">
                <PaginationPrevious />
                <template v-for="(item, index) in items" :key="index">
                  <PaginationItem
                    v-if="item.type === 'page'"
                    :value="item.value"
                    :is-active="item.value === filters.page"
                  />
                  <PaginationEllipsis v-else />
                </template>
                <PaginationNext />
              </PaginationContent>
            </Pagination>
          </template>
        </TableFooterWrapper>
      </div>
    </div>
    <router-view></router-view>
  </div>
</template>

<style scoped lang="scss">
.codes-trigger {
  @apply flex items-center gap-2 h-9 w-full min-w-[240px] rounded-md border border-input bg-transparent px-3 py-1 text-sm transition-[color,box-shadow] outline-none;
  @apply focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring/50;

  &:hover {
    @apply bg-accent/50;
  }
}

.next {
  @apply gap-4 border border-border;
  display: flex;
  padding: 4px 4px 4px 8px;
  align-items: center;
  justify-content: center;
  height: 32px;
  border-radius: 8px;
  background-color: hsl(var(--muted));
}
.arrow-right {
  @apply border border-border;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px !important;

  height: 24px;
  border-radius: 4px;
  background-color: hsl(var(--card));

  svg {
    width: 12px;
    height: 12px;
  }
}

.trigger {
  @apply inline-flex items-center gap-1 cursor-pointer select-none;
}
.content {
  @apply p-2 space-y-2 rounded-xl bg-card flex flex-col overflow-y-auto;
  max-height: 400px !important;
}
.popover-items {
  @apply flex justify-between items-center px-3 py-1 text-muted-foreground text-sm;
}
</style>
